/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, UnpackNestedValue, useForm } from "react-hook-form";
import { When } from "react-if";
import { Tabs, Tab } from "@nordcloud/gnui";
import {
  UpdateWorkflowConfigInput,
  useUpdateWorkflowConfigMutation,
  WorkflowExternalIntegrationType,
  WorkflowTriggerType,
} from "~/generated/graphql";
import { ErrorWrap, GlobalSpinner } from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useTabsWithUrl } from "~/hooks";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { getWorkflowFormSchema } from "~/views/workflows/AddWorkflow/validators";
import { WorkflowEditView } from "~/views/workflows/EditWorkflow/WorkflowEditView";
import { WorkflowSidebars } from "../constants";
import {
  getEmailInput,
  getServiceNowInput,
  getSlackInput,
  mapSelectData,
  mapSchedulerSettings,
} from "../mapping";
import { WorkflowFormData } from "../types";
import {
  SchedulerSettingsForm,
  ServiceNowSettingsForm,
  WorkflowCanvas,
  BudgetsSettingsForm,
  mapSelectDataComponent,
  mapSendDataComponents,
  mapTriggerComponents,
  AppsEnvsSettingsForm,
  SlackSettingsForm,
  AppsEnvsOrgsSettingsForm,
} from "../WorkflowCanvas";
import { EmailSettingsForm } from "../WorkflowCanvas/EmailSettingsForm/EmailSettingsForm";
import { FilterableWorkflowHistoryList } from "../WorkflowHistory/FilterableWorkflowHistoryList";
import { WorkflowProps } from "../WorkflowPropsMapping";
import { getDefaultValues } from "./helpers";

type Props = {
  workflow: WorkflowProps;
};

export function WorkflowDetailsTabs({ workflow }: Props) {
  const { activeTab, setActiveTab } = useTabsWithUrl();
  const [openSidebar, setOpenSidebar] = React.useState<WorkflowSidebars>(
    WorkflowSidebars.NONE
  );
  const closeSidebar = () => setOpenSidebar(WorkflowSidebars.NONE);
  const [edit, setEdit] = React.useState(false);

  const defaultFormValues = getDefaultValues(workflow);
  const requireServiceNowPassword =
    !defaultFormValues.sendDataComponents.includes(
      WorkflowExternalIntegrationType.ServiceNow
    );
  const requireSlackToken = !defaultFormValues.sendDataComponents.includes(
    WorkflowExternalIntegrationType.Slack
  );
  const validateSchedulerEndDate =
    !defaultFormValues.triggerComponents.includes(
      WorkflowTriggerType.Scheduler
    );
  const formMethods = useForm<UnpackNestedValue<WorkflowFormData>>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(
      getWorkflowFormSchema({
        requireServiceNowPassword,
        requireSlackToken,
        validateSchedulerEndDate,
      })
    ),
  });
  const { getValues, reset, handleSubmit, resetField } = formMethods;

  const [updateWorkflowConfig, { loading, error }] =
    useUpdateWorkflowConfigMutation({
      update(cache, { data }) {
        cache.modify({
          id: `Workflow:${data?.updateWorkflowConfig?.id}`,
          fields: {
            externalIntegrations() {
              return data?.updateWorkflowConfig?.externalIntegrations;
            },
          },
        });
      },
    });

  const {
    scheduler,
    serviceNow,
    email,
    slack,
    savingsSuggestions,
    sendDataComponents,
    selectDataComponent,
    triggerComponents,
    kpiOptions,
    costsOptions,
    budgetsOptions,
    anomalyCostsOptions,
  } = getValues();

  const handleSave = async () => {
    const input: UpdateWorkflowConfigInput = {
      selectData: mapSelectData({
        selectDataComponent,
        savingsSuggestions,
        kpiOptions,
        costsOptions,
        budgetsOptions,
        anomalyCostsOptions,
      }),
      triggers: triggerComponents.map((type) => {
        if (type === WorkflowTriggerType.Scheduler) {
          return {
            type,
            schedulerOptions: mapSchedulerSettings(scheduler),
          };
        }

        return {
          type,
        };
      }),
      externalIntegrations: sendDataComponents.map((type) => {
        switch (type) {
          case WorkflowExternalIntegrationType.ServiceNow:
            return {
              type,
              id: findExternalIntegrationId(
                workflow.externalIntegrations,
                type
              ),
              serviceNowOptions: getServiceNowInput(serviceNow),
            };
          case WorkflowExternalIntegrationType.Email:
            return {
              type,
              id: findExternalIntegrationId(
                workflow.externalIntegrations,
                type
              ),
              emailOptions: getEmailInput(email),
            };
          case WorkflowExternalIntegrationType.Slack:
            return {
              type,
              id: findExternalIntegrationId(
                workflow.externalIntegrations,
                type
              ),
              slackOptions: getSlackInput(slack),
            };
          default:
            return {
              type,
            };
        }
      }),
    };

    try {
      await updateWorkflowConfig({
        variables: {
          id: workflow.id,
          input,
        },
      });
      showSuccess(
        generateActionSuccessText(`The ${workflow.name}`)()("updated")()
      );
      resetField("schedulerSubmissionSnapshot");
    } catch {
      showError(ERROR_TEXT.default);
    }

    setEdit(false);
  };

  React.useEffect(() => {
    reset(getDefaultValues(workflow));
  }, [workflow, reset]);

  return (
    <FormProvider {...formMethods}>
      <form id="editWorkflowForm" onSubmit={handleSubmit(handleSave)}>
        <ErrorWrap error={error}>
          <GlobalSpinner loading={Boolean(loading)} />
          <Tabs handleTab={setActiveTab} step={Number(activeTab)}>
            <Tab heading="Workflow" caption="Configuration of workflow">
              <WorkflowEditView
                workflow={workflow}
                toggleEdit={setEdit}
                isEditable={edit}
                requireSlackToken={requireSlackToken}
                requireServiceNowPassword={requireServiceNowPassword}
                onCancel={() => reset(getDefaultValues(workflow))}
              />
              <When condition={!edit}>
                <WorkflowCanvas
                  formMode="read"
                  triggers={mapTriggerComponents({
                    types: triggerComponents,
                    workflow,
                    setOpenSidebar,
                  })}
                  selectDataComponent={mapSelectDataComponent({
                    type: selectDataComponent,
                    workflow,
                    setOpenSidebar,
                    isReadMode: true,
                  })}
                  sendDataComponents={mapSendDataComponents({
                    types: sendDataComponents,
                    workflow,
                    setOpenSidebar,
                  })}
                />
              </When>
            </Tab>
            <Tab caption="List of logs" heading="Workflow History">
              <FilterableWorkflowHistoryList workflowId={workflow.id} />
            </Tab>
          </Tabs>
          <SchedulerSettingsForm
            isOpen={openSidebar === "scheduler"}
            formMode="read"
            onClose={closeSidebar}
          />
          <ServiceNowSettingsForm
            isOpen={openSidebar === "serviceNow"}
            formMode="read"
            requirePassword={false}
            onClose={closeSidebar}
          />
          <EmailSettingsForm
            isOpen={openSidebar === "email"}
            workflow={workflow}
            formMode="read"
            onClose={closeSidebar}
          />

          <SlackSettingsForm
            isOpen={openSidebar === "slack"}
            formMode="read"
            requireToken={false}
            onClose={closeSidebar}
          />
          <AppsEnvsOrgsSettingsForm
            isOpen={openSidebar === "savingsSuggestions"}
            formMode="read"
            type="savingsSuggestions"
            onClose={closeSidebar}
          />
          <AppsEnvsOrgsSettingsForm
            isOpen={openSidebar === "costs"}
            formMode="read"
            type="costsOptions"
            onClose={closeSidebar}
          />
          <AppsEnvsSettingsForm
            type="anomalyCostsOptions"
            formMode="read"
            isOpen={openSidebar === "anomalyCosts"}
            applications={anomalyCostsOptions.applications ?? []}
            environments={[]}
            onClose={closeSidebar}
          />
          <BudgetsSettingsForm
            isOpen={openSidebar === "budgets"}
            formMode="read"
            onClose={closeSidebar}
          />
        </ErrorWrap>
      </form>
    </FormProvider>
  );
}

type MiniExternalIntegrationType = {
  id?: string;
  type: WorkflowExternalIntegrationType;
};

function findExternalIntegrationId(
  integrations: MiniExternalIntegrationType[],
  type: WorkflowExternalIntegrationType
) {
  const integration = integrations.find((index) => index.type === type);

  return integration?.id;
}
